import { BaseModel } from "./BaseModel";
import { ColumnHeaders, TabNames } from '../modules/Constants';

export class CalculationResultModel extends BaseModel {
    constructor() {
        super();
        this.TabName = TabNames.CALCULATION_RESULTS;
        this.TabIndex = 1;
        this.DataProcessor = new CalculationResultProcessor();
    }

}

export class CalculationResultProcessor {
    process(data)
    {
        let Calcs = data.calculations_parsed[0];
        let ExchangeRates = [];
        let Years = [];
        let Records = [];
        let Headers = [];
        let Currency = data.currency;

        let study = data.study;
        let snapshots = data.snapshots;
        let version = data.version;


        study.exchangeRates.forEach(rate => {
            rate.Item2.Values.forEach(item => {
                ExchangeRates.push({
                    currency: rate.Item1,
                    year: new Date(item.Date).getUTCFullYear(),
                    value: item.Value
                });
            });
        });


        let firstSOPYear = Math.min.apply(Math, snapshots.map(snapshot => snapshot.SaleableParts?.map(part => new Date(part?.CustomerSOP?.Value.Fields[0]).getUTCFullYear())).reduce(function (a, b) { return a.concat(b) }, []));
        let firstYearSpend = firstSOPYear - (version?.SpendYearsPriorToSOP?.Value?.Fields[0] ?? 2);
        let lastYearSpend = firstSOPYear + (version?.SpendYearsAfterSOP?.Value?.Fields[0] ?? 0);
        snapshots.forEach(snapshot => {
            snapshot.SaleableParts.forEach(part => {
                let endDate = part.CustomerVolume?.Value?.Fields[0]?.EndDate;
                if (endDate) {
                    let year = new Date(endDate).getUTCFullYear();
                    if (year > lastYearSpend) {
                        lastYearSpend = year;
                    }
                }
            });
        })

        var yearsSpending = Array.from(Array((lastYearSpend - firstYearSpend) + 1), (_, x) => x + firstYearSpend);
        Years = yearsSpending;

        //JG - Update Years in case there are additional years outside of Spending Years.
        Calcs.forEach(calc => {
            let amounts = calc?.val?.amount?.values

            amounts?.forEach(amount => {
                let year = amount.date.year;

                if (Years.findIndex(x => x === year) <= -1) {
                    Years.push(year);
                }
            })
        })

        Years.sort();

        Calcs.forEach(calc => {
            Records.push(new CalculationResultRowModel(version, calc, Years, ExchangeRates, Currency));
        })


        Headers = this.configureHeader(Years);
        let result = {
            headers: Headers,
            display: this.getDisplay(Headers, Records, Years, Currency),
            numerics: this.getNumerics(Years)
        };

        return result;
    }

    configureHeader(Years) {
        let header = [
            ColumnHeaders.ECR_NUMBER,
            ColumnHeaders.IS_INITIAL,
            ColumnHeaders.VERSION_ID,
            ColumnHeaders.OPTION_NAME,
            ColumnHeaders.CALCULATION_ID,
            ColumnHeaders.LINE_ITEM_LABEL,
            ColumnHeaders.SALEABLE_PART_NUMBER,
            ColumnHeaders.SALEABLE_PART_REV,
            ColumnHeaders.SALEABLE_PART_SUFFIX,
            ColumnHeaders.PRODUCT_GROUP,
            ColumnHeaders.DIVISION,
            ColumnHeaders.ENTITY,
            ColumnHeaders.PLANT,
            ColumnHeaders.BURDEN_CENTER,
            ColumnHeaders.VALUE_SCOPE
        ];

        Years.forEach(year => {
            header.push(year + " Amount");
        });

        header.push(
            ColumnHeaders.START_YEAR,
            ColumnHeaders.CURRENCY_PREFIX
        );

        Years.forEach(year => {
            header.push(`${year} Amount ${ColumnHeaders.CONVERTED_PREFIX}`);
        });

        header.push(
            `Total ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.START_YEAR} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.CURRENCY_PREFIX} ${ColumnHeaders.CONVERTED_PREFIX}`
        );

        return header;
    }

    getDisplay(Headers, Records, Years, Currency) {
        let display = [];

        display.push(Headers);

        Records.forEach(record => {
            let row = [
                record.EcrNumber,
                record.IsInitial,
                record.VersionODataID,
                record.OptionName,
                record.CalculationId,
                record.LineItemLabel,
                record.SaleablePartNumber,
                record.SaleablePartRevision,
                record.SaleablePartSuffix,
                record.ProductGroup,
                record.Division,
                record.Entity,
                record.Plant,
                record.BurdenCenter,
                record.ValueScope
            ]

            record.Cost.forEach(cost => {
                row.push(cost);
            });

            row.push(
                Years[0],
                record.Currency
            )

            record.CostConverted.forEach(cost => {
                row.push(cost);
            });

            row.push(
                record.CostConvertedTotal,
                Years[0],
                Currency
            )

            display.push(row);
        });

        return display;
    }

    getNumerics(Years) {
        let numeric = [];

        //set number formatting
        let startAmount = 14;
        let endAmount = Years.length + startAmount;

        let startAmountConv = endAmount + 3;
        let endAmountConv = Years.length + startAmountConv;


        numeric.push({ start: startAmount, end: endAmount });
        numeric.push({ start: startAmountConv, end: endAmountConv });

        return numeric;
    }
}

export class CalculationResultRowModel {
    EcrNumber = ""
    IsInitial = ""
    VersionODataID = ""
    OptionName = ""
    CalculationId = ""
    LineItemLabel = ""
    SaleablePartNumber = ""
    SaleablePartRevision = ""
    SaleablePartSuffix = ""
    ProductGroup = ""
    Division = ""
    Entity = ""
    Plant = ""
    ProcessType = ""
    BurdenCenter = ""
    ValueScope = ""
    Cost = [];
    CostConverted = [];
    CostConvertedTotal = 0;
    Currency = "";

    constructor(version, calculation, years, exchangeRates, currency) {
        this.EcrNumber = version?.ECRNumber?.Value?.Fields[0];
        this.IsInitial = version?.IsInitialVersion;
        this.VersionODataID = version?.id;
        this.OptionName = calculation?.val?.groupName;
        this.CalculationId = calculation?.calculationText
        this.LineItemLabel = calculation?.calculation?.value?.lineLabel
        this.SaleablePartNumber = calculation?.val?.saleablePartId?.partNumber
        this.SaleablePartRevision = calculation?.val?.saleablePartId?.drawingRevision
        this.SaleablePartSuffix = calculation?.val?.saleablePartId?.suffix
        this.ProductGroup = calculation?.val?.location?.productGroup
        this.Division = calculation?.val?.location?.division
        this.Entity = calculation?.val?.location?.entity?.name
        this.Plant = calculation?.val?.location?.plant?.plantNumber
        this.ProcessType = calculation?.val?.location?.processType
        this.BurdenCenter = `${this.ProductGroup} ${this.ProcessType} Plant ${this.Plant}, ${this.Entity}, ${this.Division}`
        this.ValueScope = calculation?.val?.valueScope;
        this.Currency = calculation?.val?.amount?.tag;

        years.forEach(year => {
            let cost = undefined;
            let convCost = undefined

            if (calculation) {
                cost = calculation?.val?.amount?.values.find(item => item.date.year === year)?.value ?? 0;

                let exchangeRate = exchangeRates?.find(rate => rate.year === year && rate.currency === currency);
                let currentCurrencyRate = exchangeRates?.find(rate => rate.year === year && rate.currency === this.Currency);

                if (exchangeRate === undefined || cost === undefined || currentCurrencyRate === undefined) {
                    if (cost !== undefined) {
                        this.CostConverted.push("Error");
                        this.Cost.push("Error");
                    } else {
                        this.CostConverted.push("");
                        this.Cost.push("");
                    }
                } else {
                    convCost = cost * exchangeRate.value / currentCurrencyRate.value;
                    this.CostConvertedTotal += convCost;

                    this.Cost.push(cost);
                    this.CostConverted.push(convCost);
                }
            }
        });
    }
}